<template>
  <div class="panel panel-default relative" :class="{'has-error': hasErrors}">
    <div class="panel-heading">
      <h2 class="panel-title">
        <b-button v-b-toggle="documentKey" @click.once="requestFiles" class="btn btn-link btn-collapse"
                  style="width: auto; padding: 2px; text-align: left; overflow: hidden;"
        >
          {{ documentName }}
        </b-button>
      </h2>
    </div>
    <b-collapse :id="documentKey" class="panel-collapse relative">
      <div class="panel-body relative">
          <document-validity-period-input
              v-if="isDocumentNeedExpire"
              :document="this.documents[0]"
              v-model="date_of_expire"
              :entity-type="this.entityType"
              :form="this.form"
              :entity-id="this.entityId"
              :document-id="this.documentId"
              :disabled="disabledDateOfExpire"
          />
        <dashboard
            v-if="dashboardShowCondition"
            :id="`dashboard-${documentKey}`"
            :key="`dashboard${$vnode.key}`"
            :props="dashboardOptions"
            :uppy="uppy"
        ></dashboard>
        <div v-if="documentsShowCondition">
          <CoolLightBox
              :items="documents || []"
              :index="applicationDocumentPreviewIndex"
              :useZoomBar="true"
              @close="applicationDocumentPreviewIndex = null"
          />
          <div class="images-wrapper" style="display: inline-flex; flex-direction: row; flex-wrap: wrap; width: 100%">
            <div style="width: 100%; text-align: center; color: #d54444"
                 v-if="documents.length === 0 && !loadingDocuments">Файлы отсутствуют
            </div>
            <div v-for="(doc, key) in documents || []" class="image"
                 :key="doc.key"
                 :style="{ backgroundImage: 'url(' + doc.thumb + ')' }"
                 style="width: 100px; height: 100px; display: inline; margin: 5px; background-repeat: no-repeat; background-size: 100px; background-position: center"
                 @click="applicationDocumentPreviewIndex = key"
            >
            </div>
          </div>
        </div>
      </div>
      <div v-if="loadingDocuments" class="loader"></div>
    </b-collapse>
    <div class="document-circles-menu">
      <div v-if="documentComment" class="document-comment" style="right: 5px; top: 6px;">
        <label>?</label>
        <div class="form-fieldhelp-text">
          <div>{{ documentComment }}</div>
        </div>
      </div>
      <div v-if="canShowDateOfExpireStatus">
        <div :class="{'date_of_expire_accept_status_yellow': !showDateOfExpireAccepted, 'date_of_expire_accept_status_green': showDateOfExpireAccepted}" style="right: 5px; top: 6px">
          <label>{{showDateOfExpireAccepted ? '✓' : '!'}}</label>
          <div class="form-fieldhelp-text">
            <div>{{showDateOfExpireAccepted ? 'Дата окончания действия документа подтверждена' : 'Дата окончания действия документа не&nbsp;подтверждена'}}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import applicationFormContextMixin from '../../mixins/applicationFormContextMixin';
import { BaseApiClient } from '../../helpers/BaseApiClient';
import CoolLightBox from 'vue-cool-lightbox';
import applicationFormMixin from '../../mixins/applicationFormMixin';
import UppyUrlDrop from '../../lib/UppyUrlDrop';
import RusUppy from '@uppy/locales/lib/ru_RU';
import Uppy from '@uppy/core';
import XHRUpload from '@uppy/xhr-upload';
import { v4 as uuidv4 } from 'uuid';
import dashboard from '@uppy/vue/src/dashboard';
import DocumentValidityPeriodInput
  from '@/common/components/application/form-inputs/visitors/DocumentValidityPeriodInput.vue';
import { UserRoles } from '@/common/enums/UserRoles';

export default {
  name: 'UppyUploader',

  components: {
    DocumentValidityPeriodInput,
    CoolLightBox,
    dashboard
  },

  mixins: [
    applicationFormContextMixin,
    applicationFormMixin
  ],

  model: {
    prop: 'documents',
    event: 'change'
  },

  props: {
    dashboardOptions: {
      type: Object,
      default: () => ({
        browserBackButtonClose: false,
        doneButtonHandler: null,
        height: 318,
        inline: true,
        note: 'Изображения и pdf до 2Мб',
        proudlyDisplayPoweredByUppy: false,
        showLinkToFileUploadResult: true,
        showProgressDetails: true,
        showRemoveButtonAfterComplete: true,
        width: '100%'
      })
    },

    form: {
      type: Object,
      default: () => ({})
    },

    dashboardShowCondition: {
      type: Boolean,
      required: true
    },

    documentComment: {
      type: String
    },

    documentId: {
      type: Number,
      required: true
    },

    documentKey: {
      type: String,
      required: true
    },

    documentName: {
      type: String
    },

    documentNeedExpire: {
      type: Number,
      required: true
    },

    documentShortName: {
      type: String
    },

    maxFilesCount: {
      type: Number
    },

    documents: {
      type: [Array, Object],
      required: true,
      default: () => []
    },

    documentsShowCondition: {
      type: Boolean,
      required: true
    },

    entityId: {
      required: true
    },

    entityType: {
      type: String,
      required: true
    },

    filesRequest: {
      type: Function
    },

    getUuid: {
      type: Function,
      required: true
    },

    hasErrors: {
      type: Boolean
    }
  },

  data() {
    let baseUrl = process.env.VUE_APP_API_URL
    RusUppy.strings.done = 'Назад'
    const uppy = new Uppy({
      id: uuidv4(),
      locale: RusUppy,
      allowMultipleUploads: true,
      autoProceed: true,
      debug: false,
      restrictions: {
        maxFileSize: 1024 * 1024 * 2,
        minFileSize: null,
        maxTotalFileSize: null,
        maxNumberOfFiles: this.maxFilesCount,
        minNumberOfFiles: 1,
        allowedFileTypes: ['application/pdf', '.jpg', '.jpeg', '.png', '.pdf', '.bmp', '.webp']
      }
    }).use(XHRUpload, {
      limit: 10,
      headers: BaseApiClient.getAuthHeaders(),
      endpoint: baseUrl + '/upload/docs',
      formData: true,
      fieldName: 'file',
      metaFields: ['uuid', 'duplicated']
    }).use(UppyUrlDrop)

    uppy.on('file-added', (file) => {
      const uuid = file.meta.uuid ?? uuidv4()
      uppy.setFileMeta(file.id, {
        uuid
      })
      this.$emit('file-added', file)
    })

    uppy.on('file-removed', (file) => {
      this.deleteDocument(file.meta.uuid)
      this.$emit('file-removed', file)
    })

    uppy.on('upload-success', (file) => {
      const uuid = (file.meta.uuid ?? uuidv4()) + '.' + file.extension.toLowerCase()

      uppy.setFileMeta(file.id, {
        uuid
      })
      file.meta.uuid = uuid
      file.meta.date_of_expire = this.date_of_expire
      this.addDocument(file)

      this.$emit('upload-success', file)
    })

    return {
      applicationDocumentPreviewIndex: null,
      loadingDocuments: false,
      date_of_expire: null,
      uppy
    }
  },

  computed: {
    canShowDateOfExpireStatus() {
      return this.isView && this.isDocumentNeedExpire
    },

    disabledDateOfExpire() {
      if (this.userIsSB) {
        return this.showDateOfExpireAccepted
      }

      return this.disabledInput
    },

    isDocumentNeedExpire() {
      return this.documentNeedExpire
    },

    showDateOfExpireAccepted() {
      return !!Number(this.documents[0]?.meta?.checked)
    },

    userIsSB () {
      return this.user.role === UserRoles.SB
    }
  },

  watch: {
    date_of_expire (value) {
      if (this.documents.length > 0) {
        this.documents.forEach((file) => {
          file.meta.date_of_expire = value
        })
        this.$emit('change', [...this.documents])
      }
    }
  },

  async mounted() {
    this.setDateOfExpire()
  },

  async updated() {
    if (!this.isView) {

      if (this.uppy.getFiles().length === this.documents.length) return

      await this.manuallyAddFiles()
    }
  },

  methods: {
    addDocument(file) {
      this.$emit('change', [...this.documents, file])
    },

    deleteDocument(uuid) {
      this.$emit('change', this.documents.filter((file) => file.meta.uuid !== uuid))
    },

    autoloadDocument(file) {
      fetch(file.src)
        .then((response) => response.blob())
        .then((blob) => {
          this.uppy.addFile({
            name: file.meta.uuid,
            type: blob.type,
            data: blob
          });
        });
    },

    getShortName() {
      return this.documentShortName
    },

    getFiles() {
      return this.uppy.getFiles()
    },

    async manuallyAddFiles() {
      for (const document of this.documents) {
        if (!document.data && document.src) {
          const blob = await (await fetch(document.src)).blob()
          document.data = blob
          document.type = blob.type
          document.name = document.meta.uuid
        }

        const fileExists = this.uppy.getFiles().some(file => file?.meta?.uuid === document?.meta?.uuid);

        if (!fileExists && document.data) {
          const fileId = this.uppy.addFile(document)

          this.uppy.setFileState(fileId, {
            progress: {
              uploadStarted: true,
              uploadComplete: true,
              percentage: 100
            }
          })
        }
      }
    },

    removeFile(id) {
      return this.uppy.removeFile(id)
    },

    async requestFiles () {
      if (this.isDraft || this.isDuplicate || this.isView || this.isEdit || (this.isNew && this.entityType === 'application')) {
        if (this.documents?.filter(doc => !!doc?.title)?.length > 0) {
          return
        }

        this.loadingDocuments = true

        if (this.filesRequest) {
          await this.filesRequest(this.documentShortName)
          if (this.isDraft || this.isDuplicate) {
            // await this.manuallyAddFiles()
          }
          this.setDateOfExpire()
        }

        this.loadingDocuments = false
      }
    },

    setDateOfExpire() {
      this.date_of_expire = this.documents?.[0]?.meta?.date_of_expire ?? this.date_of_expire
    }
  }
}
</script>
